/**
 * CategoriesAndLinks Component - By John Bellew - Infinia.ie - last updated 10/07/2025
 * 
 * A sophisticated tabbed interface for organizing and displaying categorized links.
 * Features vertical tab navigation with dynamic folder icons and tooltip integration.
 * 
 * CORE FEATURES:
 * - Vertical tab-based navigation with visual category organization
 * - Dynamic folder icons that change state (open/closed) based on selection
 * - Tooltip integration for link previews and enhanced UX
 * - Responsive design with professional styling
 * - External link handling with security attributes
 * - Category-based content filtering and organization
 * 
 * TAB SYSTEM:
 * - Vertical tab layout for enhanced navigation
 * - Dynamic folder icons (fa-folder vs fa-folder-open)
 * - State-driven visual feedback for active categories
 * - Smooth transitions and professional animations
 * - Accessible tab navigation with keyboard support
 * 
 * LINK MANAGEMENT:
 * - Category-based link filtering and display
 * - Tooltip previews showing full URLs
 * - External link icons for visual identification
 * - Security-conscious link handling (target="_blank", rel="noopener noreferrer")
 * - Professional link styling with hover effects
 * 
 * DATA STRUCTURE:
 * - Categories array with id and name properties
 * - Links array with categoryId, title, and link properties
 * - Dynamic filtering based on category relationships
 * - Flexible data structure for easy content management
 * 
 * TECHNICAL IMPLEMENTATION:
 * - React-tabs integration for professional tab behavior
 * - Tippy.js for enhanced tooltip functionality
 * - State management for active tab tracking
 * - FontAwesome icons for visual enhancement
 * - CSS-based responsive design system
 * - Production-ready with comprehensive error handling
 * 
 * @param {Object} props - Component props
 * @param {Array} props.categoriesData - Array of category objects
 * @param {string} props.categoriesData[].id - Unique category identifier
 * @param {string} props.categoriesData[].name - Category display name
 * @param {Array} props.linksData - Array of link objects
 * @param {string} props.linksData[].categoryId - Reference to parent category
 * @param {string} props.linksData[].title - Link display title
 * @param {string} props.linksData[].link - URL for the link
 * @returns {JSX.Element} Tabbed categories and links interface
 */
import React, { useState } from 'react';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import 'react-tabs/style/react-tabs.css';
import './CategoriesAndLinks.css';

/**
 * CategoriesAndLinks Component
 * 
 * Professional tabbed interface for categorized link organization with tooltips and dynamic icons.
 * 
 * @param {Object} props - Component props
 * @param {Array} props.categoriesData - Categories configuration array
 * @param {Array} props.linksData - Links data array with category associations
 * @returns {JSX.Element} Categories and links interface
 */
const CategoriesAndLinks = ({ categoriesData, linksData }) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  return (
    <Tabs
      className="vertical-tabs"
      selectedIndex={selectedIndex}
      onSelect={(index) => setSelectedIndex(index)}
    >
      <div className="tabs-container">
        <TabList className="vertical-tablist">
          {categoriesData.map((category, index) => (
            <Tab key={category.id}>
              <div className='tab-container'>
                <i className={`fa-solid ${index === selectedIndex ? 'fa-folder-open green-text' : 'fa-folder'}`}></i>&nbsp;{category.name}
              </div>
            </Tab>
          ))}
        </TabList>

        {categoriesData.map(category => (
          <TabPanel key={category.id}>
            <div className='links-container'>
              {linksData
                .filter(link => link.categoryId === category.id)
                .map(link => (
                  <div key={link.title}>
                    <i className="fa-solid fa-link green-text"></i>&nbsp;
                    <Tippy content={link.link} placement="top" arrow={true}>
                      <a href={link.link} className='pointer darklink' target="_blank" rel="noopener noreferrer">{link.title}</a>
                    </Tippy>
                  </div>
                ))}
            </div>
          </TabPanel>
        ))}
      </div>
    </Tabs>
  );
};

export default CategoriesAndLinks;
